/**
 * Redux slice for Asset Details (GraphQL version)
 * Uses GraphQL API via services-px instead of REST API
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { resetALL } from 'Redux/actions';
import { format } from 'date-fns';
import { clientQuery } from '../../../../../core/data/GraphQLApi';
import { endpoints } from '../../../../../core/defaultValues';

// GraphQL query for fetching defects
const SEARCH_DEFECTS_QUERY = `
	query SearchDefects(
		$conditions: [DefectSearchInput]
		$sort: [DefectSortInput]
		$limit: Int
		$offset: Int
	) {
		searchDefects(
			conditions: $conditions
			sort: $sort
			limit: $limit
			offset: $offset
		) {
			items {
				id
				inspectionId
				inspection {
					id
					startTime
				}
				assetId
				asset {
					id
					label
					licensePlate
					vin
				}
				driverId
				driver {
					id
					name
				}
				firstReported
				lastReported
				severity
				status
				layoutId
				typeId
				zone
				component
				condition
				repairs {
					createdOn
					status
					data
				}
				data
				createdOn
				updatedOn
			}
			total
		}
	}
`;

/**
 * Fetch defects for an asset using clientQuery
 * Note: DefectSearchInput doesn't support range filtering on lastReported,
 * so we fetch all matching defects and filter by date client-side.
 */
const fetchDefectsForAsset = async (assetId, params = {}) => {
	const {
		page = 1,
		perPage = 25,
		sort = 'severity',
		sortDir = 'desc',
		statuses,
		dates,
	} = params;

	const conditions = [];

	// Add assetId condition (assetId is a valid enumDefectSearchField)
	conditions.push({
		fields: ['assetId'],
		terms: [String(assetId)],
		exact: true,
	});

	// Add status conditions if provided (status is a valid enumDefectSearchField)
	if (statuses && statuses.length > 0) {
		conditions.push({
			fields: ['status'],
			terms: statuses.map((s) => s.toUpperCase()),
			exact: true,
		});
	}

	// Note: Range filtering on lastReported is NOT supported by the GraphQL schema
	// (lastReported is not a valid enumRangeField). We fetch more data and filter client-side.
	const fetchLimit = dates && dates.length > 0 ? 500 : perPage;
	const fetchOffset = dates && dates.length > 0 ? 0 : (page - 1) * perPage;

	const data = await clientQuery(
		SEARCH_DEFECTS_QUERY,
		{
			conditions,
			sort: [{ field: sort, order: sortDir.toUpperCase() }],
			limit: fetchLimit,
			offset: fetchOffset,
		},
		endpoints.GRAPHQL_GENERAL_V2
	);

	let defects = data.searchDefects?.items || [];

	// Filter by date range client-side (not supported by GraphQL schema)
	if (dates && dates.length > 0) {
		const { startTime, endTime } = dates[0];
		if (startTime && endTime) {
			const startDate = new Date(startTime);
			const endDate = new Date(endTime);
			defects = defects.filter((defect) => {
				const defectDate = new Date(defect.lastReported || defect.firstReported);
				return defectDate >= startDate && defectDate <= endDate;
			});
		}

		// Apply pagination after filtering
		const total = defects.length;
		const startIndex = (page - 1) * perPage;
		const paginatedDefects = defects.slice(startIndex, startIndex + perPage);

		return {
			data: paginatedDefects,
			total,
		};
	}

	return {
		data: defects,
		total: data.searchDefects?.total || 0,
	};
};

const initialState = {
	assetInspection: null,
	assetInfo: {},
	inspectionDetails: [],
	openDefects: [],
	closedDefects: [],
	totalOpenDefects: 0,
	totalClosedDefects: 0,
	loadingInspection: false,
	loadingOpenDefects: false,
	loadingClosedDefects: false,
	inspectionError: null,
	openDefectsError: null,
	closedDefectsError: null,
	// Store the PX asset ID for defect queries
	pxAssetId: null,
};

// Map severity number to label
const getSeverityLabel = (severity) => {
	const severityMap = {
		255: 'Safety Critical',
		192: 'Pre-Trip Critical',
		128: 'Safety Advisory',
		127: 'Safety Advisory',
		64: 'Maintenance Required',
	};
	return severityMap[severity] || 'Unknown';
};

// Transform defect from GraphQL format for display
const transformDefect = (defect) => {
	// Parse the data field from JSON string (GraphQL returns AWSJSON)
	let defectData = {};
	if (defect?.data) {
		try {
			defectData = typeof defect.data === 'string' ? JSON.parse(defect.data) : defect.data;
		} catch (e) {
			console.error('[transformDefect] Failed to parse defect data:', e);
		}
	}

	const lastDate = defect.lastReported
		? format(new Date(defect.lastReported), 'MM/dd/yyyy')
		: null;
	const lastTime = defect.lastReported
		? format(new Date(defect.lastReported), 'hh:mm a')
		: null;

	const firstDate = defect.firstReported
		? format(new Date(defect.firstReported), 'MM/dd/yyyy')
		: null;
	const firstTime = defect.firstReported
		? format(new Date(defect.firstReported), 'hh:mm a')
		: null;

	// Get repair info from top-level defect (GraphQL returns repairs as a sibling field, not inside data)
	const repairs = defect.repairs || [];
	const repairDate = repairs[0]?.createdOn
		? format(new Date(repairs[0].createdOn), 'MM/dd/yyyy')
		: null;
	const repairTime = repairs[0]?.createdOn
		? format(new Date(repairs[0].createdOn), 'hh:mm a')
		: null;

	// Get defect media from parsed data
	const defectMedia = defectData.defectMedia || [];
	const photos = defectMedia
		.filter((media) => media.thumbnail)
		.map((media) => ({
			mediaId: media.mediaId,
			mediaType: media.mediaType,
			thumbnail: media.thumbnail,
			url: `data:image/${media.mediaType || 'jpeg'};base64,${media.thumbnail}`,
			...(media.fileKey ? { fileKey: media.fileKey } : {}),
		}));

	// Transform repairs to include formatted dates
	const formattedRepairs = repairs.map((repair) => {
		let repairData = {};
		if (repair.data) {
			try {
				repairData = typeof repair.data === 'string' ? JSON.parse(repair.data) : repair.data;
			} catch (e) {
				repairData = {};
			}
		}
		return {
			comment: repairData.comment || null,
			mechanicFirstName: repairData.mechanicFirstName || null,
			mechanicLastName: repairData.mechanicLastName || null,
			resolution: repairData.resolution || null,
			workOrder: repairData.workOrder || null,
			status: repairData.status || null,
			createdOn: repair.createdOn,
			created: repair.createdOn ? format(new Date(repair.createdOn), 'MM/dd/yyyy') : null,
		};
	});

	return {
		id: defect.id,
		severity: defect.severity,
		severityLabel: getSeverityLabel(defect.severity),
		lastInspectedDate: lastDate,
		lastInspectedTime: lastTime,
		firstInspectedDate: firstDate,
		firstInspectedTime: firstTime,
		// Store raw timestamps for date comparison during grouping
		firstReportedRaw: defect.firstReported,
		lastReportedRaw: defect.lastReported,
		repairedDate: repairDate,
		repairedTime: repairTime,
		reconciledAssetName: defect.asset?.label || null,
		reconciledAssetId: defect.assetId || null,
		zoneLabel: defect.zone || '-',
		componentLabel: defect.component || '-',
		conditionLabel: defect.condition || '-',
		statusLabel: defect.status || '-',
		inspectionId: defect.inspectionId || null,
		inspectionType: defect.inspection?.name || defect.inspName || null,
		inspectorName: defect.driver?.name || null,
		photos,
		repairs: formattedRepairs,
	};
};

/**
 * Groups defects by Asset, Zone, Component, Condition, and Severity.
 * EVIRPX-647: Duplicate defects with same grouping criteria should be merged into a single row.
 *
 * For each group:
 * - First Inspection = earliest timestamp among all defects in group
 * - Last Inspection = most recent timestamp among all defects in group
 * - Photos = combined from all defects
 * - Repairs = combined from all defects
 *
 * @param {Array} defects - Array of transformed defects
 * @returns {Array} - Array of grouped defects (one per unique combination)
 */
const groupDefectsByKey = (defects) => {
	if (!defects || defects.length === 0) return [];

	const groups = new Map();

	defects.forEach((defect) => {
		// Create composite key from grouping fields
		const key = [
			defect.reconciledAssetName || '',
			defect.zoneLabel || '',
			defect.componentLabel || '',
			defect.conditionLabel || '',
			defect.severity || '',
		].join('|');

		if (!groups.has(key)) {
			// First defect for this key - use as base
			groups.set(key, {
				...defect,
				// Track all defect IDs in this group
				groupedDefectIds: [defect.id],
				// Store arrays for merging
				allPhotos: [...(defect.photos || [])],
				allRepairs: [...(defect.repairs || [])],
			});
		} else {
			// Merge with existing group
			const existing = groups.get(key);

			// Add this defect's ID to the group
			existing.groupedDefectIds.push(defect.id);

			// Update First Inspection to earliest date
			if (defect.firstReportedRaw && existing.firstReportedRaw) {
				if (new Date(defect.firstReportedRaw) < new Date(existing.firstReportedRaw)) {
					existing.firstReportedRaw = defect.firstReportedRaw;
					existing.firstInspectedDate = defect.firstInspectedDate;
					existing.firstInspectedTime = defect.firstInspectedTime;
				}
			} else if (defect.firstReportedRaw && !existing.firstReportedRaw) {
				existing.firstReportedRaw = defect.firstReportedRaw;
				existing.firstInspectedDate = defect.firstInspectedDate;
				existing.firstInspectedTime = defect.firstInspectedTime;
			}

			// Update Last Inspection to most recent date
			if (defect.lastReportedRaw && existing.lastReportedRaw) {
				if (new Date(defect.lastReportedRaw) > new Date(existing.lastReportedRaw)) {
					existing.lastReportedRaw = defect.lastReportedRaw;
					existing.lastInspectedDate = defect.lastInspectedDate;
					existing.lastInspectedTime = defect.lastInspectedTime;
				}
			} else if (defect.lastReportedRaw && !existing.lastReportedRaw) {
				existing.lastReportedRaw = defect.lastReportedRaw;
				existing.lastInspectedDate = defect.lastInspectedDate;
				existing.lastInspectedTime = defect.lastInspectedTime;
			}

			// Merge photos (avoid duplicates by mediaId)
			if (defect.photos && defect.photos.length > 0) {
				const existingMediaIds = new Set(existing.allPhotos.map((p) => p.mediaId));
				defect.photos.forEach((photo) => {
					if (!existingMediaIds.has(photo.mediaId)) {
						existing.allPhotos.push(photo);
					}
				});
			}

			// Merge repairs (avoid duplicates by createdOn timestamp)
			if (defect.repairs && defect.repairs.length > 0) {
				const existingRepairTimes = new Set(existing.allRepairs.map((r) => r.createdOn));
				defect.repairs.forEach((repair) => {
					if (!existingRepairTimes.has(repair.createdOn)) {
						existing.allRepairs.push(repair);
					}
				});
			}
		}
	});

	// Convert Map to array and finalize merged data
	return Array.from(groups.values()).map((group) => ({
		...group,
		// Replace photos and repairs with merged arrays
		photos: group.allPhotos,
		repairs: group.allRepairs,
		// Clean up temporary fields
		allPhotos: undefined,
		allRepairs: undefined,
	}));
};

// Helper to get translated label from translatable string object
const getTranslatedLabel = (translatableObj) => {
	if (!translatableObj) return null;
	if (typeof translatableObj === 'string') return translatableObj;
	if (typeof translatableObj === 'object' && translatableObj.languageLabel) {
		return translatableObj.languageLabel;
	}
	if (typeof translatableObj === 'object') {
		return translatableObj.en || translatableObj['en-us'] || Object.values(translatableObj)[0];
	}
	return null;
};

// Imperial units for finding correct index in conversion pairs
const imperialUnits = ['mi', 'gal', 'mph', 'mpg', 'psi', 'F', 'oz', 'lb', 'ton', 'ft', 'in', 'yd', 'hrs'];

// Helper to find imperial unit index in conversion pair
const findImperialIndex = (unitPair) => {
	if (!unitPair || !Array.isArray(unitPair) || unitPair.length <= 1) return 0;
	const idx = unitPair.findIndex((unit) => imperialUnits.includes(unit));
	return idx >= 0 ? idx : 0;
};

// Helper to format array value with unit
const formatArrayValue = (content, detail) => {
	const unitPair = detail.inspectionDetailUnitConversionPair;
	const unitIndex = findImperialIndex(unitPair);
	const rawValue = content[unitIndex] ?? content[0];

	// Skip if value is empty, 0, null, or undefined
	if (rawValue === '' || rawValue === 0 || rawValue === null || rawValue === undefined) return null;

	// Parse as number and format
	const numValue = parseFloat(rawValue);
	if (isNaN(numValue)) return null;

	const value = Math.round(numValue).toLocaleString();

	// Get the correct unit from conversion pair or selected unit
	let unit = '';
	if (Array.isArray(unitPair) && unitPair.length > unitIndex) {
		unit = unitPair[unitIndex];
	} else if (detail.inspectionDetailSelectedUnit) {
		unit = detail.inspectionDetailSelectedUnit;
	}

	return unit ? `${value} ${unit}` : value;
};

// Helper to parse detail value - handles arrays and boolean strings
const parseDetailValue = (detail) => {
	const content = detail?.inspectionDetailContent;
	if (!content || content === '') return null;

	// Handle boolean strings
	if (content === 'true') return 'Yes';
	if (content === 'false') return 'No';

	// Handle arrays (API returns actual arrays, not JSON strings)
	if (Array.isArray(content) && content.length > 0) {
		return formatArrayValue(content, detail);
	}

	// Try to parse JSON string (fallback for string-encoded arrays)
	if (typeof content === 'string') {
		try {
			const parsed = JSON.parse(content);
			if (Array.isArray(parsed) && parsed.length > 0) {
				return formatArrayValue(parsed, detail);
			}
		} catch (e) {
			// Not JSON, return as-is
		}
	}

	return content;
};

// Fetch asset details from the Asset API and last inspection
// Note: assetId should always be the PX integer ID
// companyId is inferred by the backend from the user's auth token
export const getAssetInspection = createAsyncThunk(
	'assetDetails/getAssetInspection',
	async ({ assetId }, { rejectWithValue }) => {
		try {
			// First get the asset to get its label using clientQuery
			let asset = null;
			try {
				const query = `
					query GetAsset($id: Int!) {
						getAsset(id: $id) {
							id
							label
							unitId
							assetTypeId
							assetType {
								id
								label
							}
							vin
							make
							model
							year
							licensePlate
							isActive
						}
					}
				`;
				const response = await clientQuery(
					query,
					{ id: Number(assetId) },
					endpoints.GRAPHQL_GENERAL_V2
				);
				asset = response?.getAsset || null;
			} catch (err) {
				console.warn('[getAssetInspection] getAsset failed:', err.message);
			}

			// Get the last inspection for this asset using searchInspections
			// Filter client-side by assetId since it's stored in the data JSON
			let lastInspection = null;
			try {
				const endTime = new Date().toISOString();
				const startTime = new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString();

				const inspectionQuery = `
					query SearchInspections(
						$conditions: [InspectionSearchInput]
						$sort: [InspectionSortInput]
						$limit: Int
					) {
						searchInspections(
							conditions: $conditions
							sort: $sort
							limit: $limit
						) {
							items {
								id
								startTime
								driver {
									name
								}
								requiresReview
								data
							}
							total
						}
					}
				`;

				const inspectionResponse = await clientQuery(
					inspectionQuery,
					{
						conditions: [
							{
								range: {
									fields: ['startTime'],
									GTE: startTime,
									LTE: endTime,
								},
							},
						],
						sort: [{ field: 'startTime', order: 'DESC' }],
						limit: 1000,
					},
					endpoints.GRAPHQL_GENERAL_V2
				);

				const inspections = inspectionResponse.searchInspections?.items || [];
				const targetAssetId = Number(assetId);
				const matchingInspections = [];

				for (const inspection of inspections) {
					if (!inspection.data) continue;

					try {
						const inspData = typeof inspection.data === 'string'
							? JSON.parse(inspection.data)
							: inspection.data;

						const hasAsset = inspData.inspectionAssets?.some(
							(ia) => ia.asset?.assetId === targetAssetId || Number(ia.asset?.assetId) === targetAssetId
						);

						if (hasAsset) {
							const detailData = inspData.inspectionDetail?.inspectionDetailData || [];
							matchingInspections.push({
								inspection,
								detailDataCount: detailData.length,
								startTime: inspection.startTime,
								requiresReview: inspection.requiresReview,
							});
						}
					} catch (e) {
						continue;
					}
				}

				if (matchingInspections.length > 0) {
					// Prefer requiresReview=true, more detailData, then newer
					matchingInspections.sort((a, b) => {
						if (a.requiresReview && !b.requiresReview) return -1;
						if (!a.requiresReview && b.requiresReview) return 1;
						if (a.detailDataCount !== b.detailDataCount) {
							return b.detailDataCount - a.detailDataCount;
						}
						return new Date(b.startTime) - new Date(a.startTime);
					});
					lastInspection = matchingInspections[0].inspection;
				}
			} catch (err) {
				console.warn('[getAssetInspection] getLastInspection failed:', err.message);
			}

			return { assetId, asset, lastInspection };
		} catch (error) {
			console.error('[getAssetInspection] Error:', error);
			return rejectWithValue(
				error.message || 'Failed to fetch asset details'
			);
		}
	}
);

// Fetch open and pending defects for an asset using GraphQL
// companyId is inferred by the backend from the user's auth token
export const getOpenAssetDefects = createAsyncThunk(
	'assetDetails/getOpenAssetDefects',
	async ({ assetId, page = 1, perPage = 5, sort = 'severity', sortDir = 'desc', dates }, { rejectWithValue }) => {
		try {
			// Transform dates array to date filter format
			const dateFilters = dates && dates.length >= 2
				? [{ startTime: `${dates[0]}T00:00:00Z`, endTime: `${dates[1]}T23:59:59Z` }]
				: [];

			const response = await fetchDefectsForAsset(assetId, {
				page,
				perPage,
				sort,
				sortDir,
				statuses: ['open', 'pending'],
				dates: dateFilters,
			});

			const transformedDefects = response.data.map(transformDefect);

			return {
				defects: transformedDefects,
				totalCount: response.total,
			};
		} catch (error) {
			return rejectWithValue(
				error.message || 'Failed to fetch open defects'
			);
		}
	}
);

// Fetch closed and ignored defects for an asset using GraphQL
// companyId is inferred by the backend from the user's auth token
export const getClosedAssetDefects = createAsyncThunk(
	'assetDetails/getClosedAssetDefects',
	async ({ assetId, page = 1, perPage = 5, sort = 'severity', sortDir = 'desc', dates }, { rejectWithValue }) => {
		try {
			// Transform dates array to date filter format
			const dateFilters = dates && dates.length >= 2
				? [{ startTime: `${dates[0]}T00:00:00Z`, endTime: `${dates[1]}T23:59:59Z` }]
				: [];

			const response = await fetchDefectsForAsset(assetId, {
				page,
				perPage,
				sort,
				sortDir,
				statuses: ['ignored', 'repaired'],
				dates: dateFilters,
			});

			const transformedDefects = response.data.map(transformDefect);

			return {
				defects: transformedDefects,
				totalCount: response.total,
			};
		} catch (error) {
			return rejectWithValue(
				error.message || 'Failed to fetch closed defects'
			);
		}
	}
);

const assetDetailsPxSlice = createSlice({
	name: 'assetDetails',
	initialState,
	reducers: {
		reset: () => initialState,
		// Action to set asset info from navigation state (passed from inspection details page)
		// The assetData comes from the transformAssetData function in inspectionDetails slice
		setAssetInfoFromNavigation: (state, action) => {
			const assetData = action.payload;

			// Basic asset info - assetData comes from the inspection details transformAssetData
			state.assetInfo = {
				assetNumber: assetData?.assetName || 'N/A',
				assetId: assetData?.assetId,
				equipmentType: assetData?.zoneLayoutName || 'N/A',
				reconciledAssetCategory: assetData?.assetCategory,
			};

			// Build inspection details from addtlAssetInfo
			// The addtlAssetInfo contains DOT, VIN, POWER UNIT, RENTAL, ASSET LOCATION
			state.inspectionDetails = (assetData?.addtlAssetInfo || [])
				.filter((info) => info.addtlAssetContent && info.addtlAssetContent !== '—')
				.map((info) => ({
					label: info.languageLabel || 'Unknown',
					content: info.addtlAssetContent,
				}));

			// Store the asset ID for defect queries
			state.pxAssetId = assetData?.assetId;
		},
	},
	extraReducers: (builder) => {
		// Get Asset Inspection
		builder
			.addCase(getAssetInspection.pending, (state) => {
				state.loadingInspection = true;
				state.inspectionError = null;
			})
			.addCase(getAssetInspection.fulfilled, (state, action) => {
				state.loadingInspection = false;
				state.inspectionError = null;

				const { assetId, asset, lastInspection } = action.payload;
				state.pxAssetId = assetId;

				// Parse the last inspection data JSON
				let inspectionData = {};
				if (lastInspection?.data) {
					try {
						inspectionData = typeof lastInspection.data === 'string'
							? JSON.parse(lastInspection.data)
							: lastInspection.data;
					} catch (e) {
						console.error('[getAssetInspection] Failed to parse inspection data:', e);
					}
				}

				// Get inspectionDetailData from the last inspection
				const inspectionDetailData = inspectionData?.inspectionDetail?.inspectionDetailData || [];

				// Get asset info from inspection data or asset record
				const inspectionAsset = inspectionData?.inspectionAssets?.[0]?.asset;

				// Basic asset info - prefer inspection data for equipment type
				state.assetInfo = {
					assetNumber: asset?.label || inspectionAsset?.assetName || 'N/A',
					assetId: asset?.id || assetId,
					equipmentType: inspectionData?.inspectionAssets?.[0]?.zoneLayoutName?.languageLabel
						|| asset?.assetType?.label
						|| 'N/A',
					reconciledAssetCategory: inspectionAsset?.assetCategory,
				};

				// Build inspection details from inspectionDetailData (telemetry from last inspection)
				// This includes fields like ODOMETER, ENGINE HOURS, FUEL LEVEL, HAZMAT, etc.
				const details = inspectionDetailData
					.filter((detail) => detail.inspectionDetailContent !== '')
					.map((detail) => ({
						label: getTranslatedLabel(detail.inspectionDetailDataName)
							|| detail.inspectionDetailCategory
							|| 'Unknown',
						content: parseDetailValue(detail),
						unit: detail.inspectionDetailSelectedUnit || detail.inspectionDetailUnitConversionPair || null,
						category: detail.inspectionDetailCategory,
					}))
					.filter((detail) => detail.content !== null);

				// If no inspection details from last inspection, fall back to asset record fields
				if (details.length === 0 && asset) {
					if (asset.vin) {
						details.push({ label: 'VIN', content: asset.vin });
					}
					if (asset.licensePlate) {
						details.push({ label: 'License Plate', content: asset.licensePlate });
					}
					if (asset.make) {
						details.push({ label: 'Make', content: asset.make });
					}
					if (asset.model) {
						details.push({ label: 'Model', content: asset.model });
					}
					if (asset.year) {
						details.push({ label: 'Year', content: String(asset.year) });
					}
					if (asset.powerUnitNumber) {
						details.push({ label: 'Power Unit', content: asset.powerUnitNumber });
					}
					if (asset.hazardousMaterials) {
						details.push({ label: 'HAZMAT', content: asset.hazardousMaterials });
					}
				}

				state.inspectionDetails = details;
			})
			.addCase(getAssetInspection.rejected, (state, action) => {
				state.loadingInspection = false;
				state.inspectionError = action.payload;
			});

		// Get Open Asset Defects
		builder
			.addCase(getOpenAssetDefects.pending, (state) => {
				state.loadingOpenDefects = true;
				state.openDefectsError = null;
			})
			.addCase(getOpenAssetDefects.fulfilled, (state, action) => {
				state.loadingOpenDefects = false;
				state.openDefectsError = null;
				// EVIRPX-647: Group defects by Asset/Zone/Component/Condition/Severity
				// to avoid duplicate rows for the same defect combination
				const groupedDefects = groupDefectsByKey(action.payload.defects);
				state.openDefects = groupedDefects;
				state.totalOpenDefects = groupedDefects.length;
			})
			.addCase(getOpenAssetDefects.rejected, (state, action) => {
				state.loadingOpenDefects = false;
				state.openDefectsError = action.payload;
			});

		// Get Closed Asset Defects
		builder
			.addCase(getClosedAssetDefects.pending, (state) => {
				state.loadingClosedDefects = true;
				state.closedDefectsError = null;
			})
			.addCase(getClosedAssetDefects.fulfilled, (state, action) => {
				state.loadingClosedDefects = false;
				state.closedDefectsError = null;
				state.closedDefects = action.payload.defects;
				state.totalClosedDefects = action.payload.totalCount;
			})
			.addCase(getClosedAssetDefects.rejected, (state, action) => {
				state.loadingClosedDefects = false;
				state.closedDefectsError = action.payload;
			})

			// Global reset
			.addCase(resetALL, () => initialState);
	},
});

export const { reset, setAssetInfoFromNavigation } = assetDetailsPxSlice.actions;

export default assetDetailsPxSlice.reducer;
